import BubbleCommon, {
  keywordDrillUrlFormItemTips,
} from '@op/components/BubbleCommon';
import React, { useMemo, useRef } from 'react';
import { Button, Form, Icon, Input, InputAdornment } from 'tea-component';
import { useIntl } from 'umi';
import { NO_DATE } from '@op/constants';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { KeywordUrlTabType, URLListType } from '../../../../types';
import { useCheckUrlPattern } from '../../../../hook';
import _ from 'lodash-es';

export default function UrlOptions({
  tab,
  urlList,
  setUrlList,
}: {
  tab: KeywordUrlTabType;
  urlList: URLListType[];
  setUrlList: React.Dispatch<React.SetStateAction<URLListType[]>>;
}) {
  const { formatMessage } = useIntl();
  const keyAutoIndex = useRef(1);
  const { status, message } = useMemo(() => {
    const isError = urlList.some((item) => item?.status === 'error');
    const status: 'error' | undefined = isError ? 'error' : undefined;
    const message = isError ? 'Unable to recognize the link' : undefined;
    return { status, message };
  }, [urlList]);

  function getOperateBtn(index: number) {
    const disabled = index === 0 && urlList.length >= 20;

    let text = index === 0 ? 'Add URL' : 'Delete URL';
    if (disabled) {
      text = 'Only 20 URLs can be added';
    }

    return (
      <BubbleCommon
        showIcon={false}
        placement="top"
        tipsMap={undefined}
        childClassName="dbo-end-btn-bubble"
        content={formatMessage({ id: text })}
      >
        <Button
          type="weak"
          className=" dbo-end-btn ns-btn--icon"
          disabled={disabled}
          onClick={() => {
            if (index === 0) {
              keyAutoIndex.current = keyAutoIndex.current + 1;
              setUrlList([
                ...urlList,
                { text: '', value: keyAutoIndex.current, status: 'success' },
              ]);
            } else {
              setUrlList(urlList.filter((_, i) => i !== index));
            }
          }}
        >
          <Icon type={index === 0 ? 'plus' : 'delete'} />
        </Button>
      </BubbleCommon>
    );
  }

  function onChange(item: Omit<URLListType, 'value'>, index: number) {
    setUrlList((list: URLListType[]) => {
      list[index] = { ...list[index], ...item };
      return [...list];
    });
  }

  if (tab !== 'url') return null;

  return (
    <Form.Item
      className="dbo-keyword-url-filter-box"
      status={status}
      showStatusIcon={false}
      message={formatMessage({ id: message || NO_DATE.EMPTY })}
      label={
        <BubbleCommon tipsMap={keywordDrillUrlFormItemTips} placement="top">
          {formatMessage({ id: 'URL' })}
        </BubbleCommon>
      }
    >
      {urlList.map((item, index) => {
        return (
          <UrlInputItem
            item={item}
            index={index}
            key={item.value}
            getOperateBtn={getOperateBtn}
            onChange={onChange}
          ></UrlInputItem>
        );
      })}
    </Form.Item>
  );
}

function UrlInputItem({
  item,
  index,
  getOperateBtn,
  onChange,
}: {
  item: URLListType;
  index: number;
  getOperateBtn: (index: number) => JSX.Element;
  onChange: (item: Omit<URLListType, 'value'>, index: number) => void;
}) {
  const { loading, checkUrlList } = useCheckUrlPattern();

  const inputText = useMemo(() => {
    return _.debounce(async (value: string) => {
      const data = await checkUrlList(value);
      const isError = data.includes(value);
      onChange({ status: isError ? 'error' : 'success' } as any, index);
    }, 500);
  }, []);

  return (
    <div key={item.value} className="dbo-keyword-url-item">
      <InputAdornment
        className={`dbo-keyword-url-input ${
          item.status === 'error' ? 'dbo-error' : ''
        }`}
        before={
          <Button className="dbo-icon-btn" type="weak">
            {loading ? (
              <Icon type="loading" />
            ) : (
              <SvgIcon type={'share-link'} />
            )}
          </Button>
        }
      >
        <Input
          value={item.text}
          onChange={(value: string) => {
            let realValue = value.trim();
            onChange({ text: realValue, status: 'loading' }, index);
            inputText(realValue);
          }}
        />
        {item.text && (
          <Icon
            type="dismiss"
            className="dbo-clear-btn"
            onClick={() => {
              onChange({ text: '', status: 'success' }, index);
            }}
          ></Icon>
        )}
      </InputAdornment>

      {getOperateBtn(index)}
    </div>
  );
}
